import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, repeat, shareReplay, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from './authentication.service';

/**
 * What every ApiService call errors with. Callers across the app read
 * `err?.error?.detail` (FastAPI's error body) and `err.status`, so those are
 * preserved here - throwing a bare `Error` used to drop both, so every
 * specific backend message (403 permission, 400 validation, ...) fell back to
 * generic copy. `error.detail` is always a readable string or null (422
 * validation arrays are flattened; object details such as
 * `{"detail": ..., "message": ...}` use their inner string); `error.raw` keeps
 * the unmodified response body; `message` keeps the "Request error (400): ..."
 * form.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public error: { detail: string | null; raw: unknown; [key: string]: unknown },
    public detail: string | null
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(
    private http: HttpClient,
    public authenticationService: AuthenticationService
  ) {}

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  httpFileOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'multipart/form-data'
    })
  };

  /**
   * Angular's HttpParams stringifies undefined/null values to the literal
   * string "undefined"/"null" rather than omitting them, which backend RSQL
   * filter parsing (and other strict query parsing) rejects outright. Strip
   * those keys before they ever reach HttpParams.
   */
  private cleanParams(params: any): any {
    if (!params) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
    );
  }

  getAPI<Type>(endpoint: string, queryParams?: any, pollInterval?: number): Observable<any> {
    const parameters = this.cleanParams(queryParams);
    if (pollInterval) {
      return this.http
        .get<Type>(`${environment.apiUrl}/${endpoint}`, {
          observe: 'response',
          responseType: 'json',
          params: parameters
        })
        .pipe(
          catchError(this.handleError),
          // shareReplay(),
          repeat({ delay: pollInterval })
          // repeat({ delay: pollInterval, count: 1 })
        );
    } else {
      return this.http
        .get<Type>(`${environment.apiUrl}/${endpoint}`, {
          observe: 'response',
          responseType: 'json',
          params: parameters
        })
        .pipe(catchError(this.handleError), shareReplay(1));
    }
  }

  postAPI<Type>(endpoint: string, data: any, queryParams: any = {}): Observable<any> {
    return this.http
      .post<Type>(`${environment.apiUrl}/${endpoint}`, data, {
        observe: 'response',
        responseType: 'json',
        params: queryParams
      })
      .pipe(catchError(this.handleError));
  }

  deleteAPI<Type>(endpoint: string, query_id: number): Observable<any> {
    return this.http
      .delete<Type>(`${environment.apiUrl}/${endpoint}/${query_id}`, {
        observe: 'response',
        responseType: 'json'
      })
      .pipe(catchError(this.handleError));
  }

  deleteAllAPI<Type>(endpoint: string): Observable<any> {
    return this.http
      .delete<Type>(`${environment.apiUrl}/${endpoint}/`, {
        observe: 'response',
        responseType: 'json'
      })
      .pipe(catchError(this.handleError));
  }

  putAPI<Type>(endpoint: string, query_id: number, data: any): Observable<any> {
    return this.http
      .put<Type>(`${environment.apiUrl}/${endpoint}/${query_id}`, data, {
        observe: 'response',
        responseType: 'json'
      })
      .pipe(catchError(this.handleError));
  }

  // For PUT endpoints whose path doesn't end in a simple numeric ID (e.g.
  // coverage/zones/{id}/products) - putAPI above always appends /{query_id}.
  putAtPathAPI<Type>(endpoint: string, data: any): Observable<any> {
    return this.http
      .put<Type>(`${environment.apiUrl}/${endpoint}`, data, {
        observe: 'response',
        responseType: 'json'
      })
      .pipe(catchError(this.handleError));
  }

  patchAPI<Type>(endpoint: string, data: any): Observable<any> {
    return this.http
      .patch<Type>(`${environment.apiUrl}/${endpoint}`, data, {
        observe: 'response',
        responseType: 'json'
      })
      .pipe(catchError(this.handleError));
  }

  postFileAPI<Type>(endpoint: string, data: any): Observable<any> {
    return this.http
      .post<Type>(`${environment.apiUrl}/${endpoint}`, data, {
        observe: 'response',
        responseType: 'json'
      })
      .pipe(catchError(this.handleError));
  }

  getFileAPI(endpoint: string, query_id: string = '', queryParams?: any): Observable<any> {
    let parameters = {};
    if (queryParams) {
      parameters = queryParams;
    }

    let url = `${environment.apiUrl}/${endpoint}`;

    if (query_id !== '') {
      url = `${environment.apiUrl}/${endpoint}/${query_id}`;
    }

    return this.http
      .get(url, {
        observe: 'response',
        responseType: 'blob',
        params: parameters
      })
      .pipe(catchError(this.handleError));
  }

  downloadFileAPI(endpoint: string, query_id: string = '', queryParams?: any): Observable<any> {
    return this.getFileAPI(endpoint, query_id, queryParams);
  }

  /**
   * Calls GET /<endpoint>/download with the supplied query params (q, filter, sort).
   * Returns the raw HttpResponse so the caller can read Content-Disposition.
   */
  downloadCsvAPI(endpoint: string, queryParams?: Record<string, unknown>): Observable<any> {
    return this.getFileAPI(`${endpoint}/download`, '', queryParams as any);
  }

  /**
   * A readable string from a FastAPI `detail`: the string itself, or - when
   * the backend sends an object such as `{"detail": ..., "message": ...}`
   * (portal payment errors) - its inner `detail`/`message` string.
   */
  private static detailText(detail: unknown): string | null {
    if (typeof detail === 'string') return detail || null;
    if (detail && typeof detail === 'object') {
      const inner = detail as { detail?: unknown; message?: unknown };
      for (const value of [inner.detail, inner.message]) {
        if (typeof value === 'string' && value) return value;
      }
    }
    return null;
  }

  handleError(error: HttpErrorResponse) {
    // Services that re-apply `catchError(this.api.handleError)` on top of an
    // ApiService call would otherwise re-wrap it, losing `raw`.
    if ((error as unknown) instanceof ApiError) return throwError(() => error);

    console.error('HTTP Error:', error);

    let errorMessage = 'An unexpected error occurred. Please try again later.';
    // Only a message the backend actually sent - never Angular's generic
    // "Http failure response for ..." text - so callers' own friendly
    // fallback copy still applies when there's nothing specific to show.
    let serverDetail: string | null = null;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client-side error: ${error.error.message || 'Unknown error'}`;
    } else {
      // Server-side error
      // FastAPI's 422 validation errors put `detail` as an array of
      // {loc, msg, type} objects, not a string - interpolating that array
      // directly used to stringify to "[object Object],[object Object]"
      // with no useful information. Join each item's field path + message
      // instead so validation failures are actually readable.
      const detail = error.error?.detail;
      let serverMessage: string;
      if (Array.isArray(detail)) {
        serverMessage = detail
          .map((item: any) => {
            const field = Array.isArray(item?.loc) ? item.loc.slice(1).join('.') : null;
            return field ? `${field}: ${item?.msg}` : item?.msg || JSON.stringify(item);
          })
          .join('; ');
        serverDetail = serverMessage;
      } else {
        serverDetail = ApiService.detailText(detail);
        serverMessage = serverDetail || error.message || 'No message available';
      }
      // 4xx is a client-side/request problem (not found, bad input, no
      // permission) - only 5xx (and 0, a network/connection failure) is
      // an actual server error. Mislabeling every non-2xx response
      // "Server error" made e.g. a 404 read as "the backend broke" when
      // the real issue was the requested record not existing.
      const errorLabel =
        error.status === 0
          ? 'Network error'
          : error.status >= 500
            ? 'Server error'
            : 'Request error';
      errorMessage = `${errorLabel} (${error.status}): ${serverMessage}`;

      switch (error.status) {
        case 401:
          console.warn('Unauthorized (401): Redirecting to login...');
          // Handle unauthorized errors (e.g., redirect to login)
          break;
        case 403:
          console.warn('Forbidden (403): Access denied.');
          break;
        case 404:
          console.warn('Not Found (404): Resource unavailable.');
          break;
        case 500:
          console.warn('Internal Server Error (500): Something went wrong on the server.');
          break;
        default:
          console.warn(`Unhandled HTTP status: ${error.status}`);
      }
    }

    // Not `this.`-dependent: several services pass this method unbound to
    // catchError.
    const body =
      error.error && typeof error.error === 'object' && !(error.error instanceof Blob)
        ? { ...error.error, detail: serverDetail, raw: error.error }
        : { detail: serverDetail, raw: error.error };
    return throwError(() => new ApiError(errorMessage, error.status, body, serverDetail));
  }
}
